import { FC } from 'react';
import { useHistory } from 'react-router';
import { Center, Divider, HStack, Stack } from '@chakra-ui/react';

import {
  CardProductId,
  FeatureFilterField,
  FeatureName,
  UserFilterValues,
} from '__generated__/GQL';
import { isCreditCardPendingOrder } from 'common/creditCards/isCreditCardPendingOrder';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCompanyUserId from 'common/hooks/useCompanyUserId/useCompanyUserId';
import {
  formatExpiryDate,
  formatPan,
  getCardAssetSrc,
  isOldCardDesign,
} from 'common/paymentCards/utils';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import { useFakeTemporaryVirtualPaymentCardIdContext } from 'features/PaymentCards/context';
import { formatDate } from 'helpers/date';

import NavigationHeader from '../../../../../components/NavigationHeader';
import { CreditCard } from '../../../../../hooks/useCreditCards';
import PaymentCardStatusBadge from '../../PaymentCardStatusBadge';
import { useCardDetailsContext } from '../context';
import CardAlias from './CardAlias';
import CardLimits from './CardLimits';
import CardOptions from './CardOptions';
import CardSecurityDetails from './CardSecurityDetails';
import locales from './locales';
import * as S from './styles';
import { getCardActions, getCardLabelStyle, getCardPanStyle } from './utils';

interface OperationListProps {
  creditCard: CreditCard;
}

const OperationList: FC<OperationListProps> = ({ creditCard }) => {
  const history = useHistory();
  const fakeTemporaryVirtualPaymentCardId =
    useFakeTemporaryVirtualPaymentCardIdContext();
  const {
    cardDetails: { CVV, PAN },
  } = useCardDetailsContext();

  const shouldShowDetails = PAN && CVV;
  const { companyUserId } = useCompanyUserId();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  if (isCreditCardPendingOrder(creditCard)) {
    return (
      <>
        <NavigationHeader />

        <Center marginBottom="space-24">
          <S.CardAssetContainer>
            <S.StatusBadgeContainer>
              <PaymentCardStatusBadge creditCard={creditCard} />
            </S.StatusBadgeContainer>

            <S.CardImage
              alt="Card"
              creating={true}
              isActive={false}
              src={getCardAssetSrc(creditCard)}
            />
          </S.CardAssetContainer>
        </Center>
      </>
    );
  }

  const {
    alias,
    cardHolderName,
    creditCardId,
    deactivateAt,
    expiryDate,
    isPhysical,
    maskedPan,
    pinTryExceeds,
    status,
  } = creditCard;

  const isLocked = status !== 'ACTIVE' || pinTryExceeds;
  const isOwner = companyUserId === creditCard.companyUserId;
  const cardAssetUrl = getCardAssetSrc(creditCard);
  const cardPanStyle = getCardPanStyle(creditCard);
  const cardLabelStyle = getCardLabelStyle(creditCard);

  const isLive = status === 'ACTIVE' || status === 'LOCKED';

  const onUpdateDeactivationDateClicked = () => {
    history.push(`/cards/${creditCardId}/deactivation-date`);
  };

  const isOldDesign = isOldCardDesign(creditCard);
  const canRequestPaymentCards = canAccessFeature({
    name: FeatureName.RequestPaymentcards,
    where: {
      or: [{ field: FeatureFilterField.User, values: [UserFilterValues.Self] }],
    },
  });
  const canOrderPaymentCards = canAccessFeature({
    name: FeatureName.PaymentcardsOrder,
  });
  const canAliasCard = canAccessFeature({
    name: FeatureName.Paymentcards,
  });

  const cardActions = getCardActions({
    canOrderPaymentCards,
    canRequestPaymentCards,
    creditCard,
    history,
    isOwner,
  });

  const isBudget = creditCard.cardProductId === CardProductId.Budget;

  const isFakeTemporaryVirtualCard =
    fakeTemporaryVirtualPaymentCardId === creditCardId;

  const canUpdateDeactivationDate = !isPhysical && !isBudget && deactivateAt;

  return (
    <>
      <NavigationHeader />

      <Center marginBottom="space-24">
        <S.CardAssetContainer>
          {!isOldDesign ? (
            <S.StatusBadgeContainer>
              <PaymentCardStatusBadge creditCard={creditCard} />
            </S.StatusBadgeContainer>
          ) : null}

          <S.CardImage
            alt="Card"
            isActive={status === 'ACTIVE'}
            src={cardAssetUrl}
          />

          {isFakeTemporaryVirtualCard ? null : (
            <S.CardInformationContainer>
              {/* pan */}
              <Text
                color={`${cardPanStyle.color}.${cardPanStyle.colorVariant}`}
                marginBottom="space-4"
                variant="primary"
              >
                {shouldShowDetails ? formatPan(PAN) : formatPan(maskedPan)}
              </Text>

              <HStack spacing="space-8">
                {/* cvv */}
                <Text
                  color={`${cardLabelStyle.color}.${cardLabelStyle.colorVariant}`}
                  variant="primary"
                >
                  {locales.cvv}

                  <Text
                    as="span"
                    color={`${cardPanStyle.color}.${cardPanStyle.colorVariant}`}
                    display="inline"
                  >
                    {shouldShowDetails ? CVV : '•••'}
                  </Text>
                </Text>

                {/* expiration date */}
                <Text
                  color={`${cardLabelStyle.color}.${cardLabelStyle.colorVariant}`}
                  variant="primary"
                >
                  {locales.expiryDate}

                  <Text
                    as="span"
                    color={`${cardPanStyle.color}.${cardPanStyle.colorVariant}`}
                    display="inline"
                    variant="primary"
                  >
                    {shouldShowDetails ? formatExpiryDate(expiryDate) : '••/••'}
                  </Text>
                </Text>
              </HStack>

              {isOldDesign ? (
                <S.OldDesignStatusBadgeContainer>
                  <PaymentCardStatusBadge creditCard={creditCard} />
                </S.OldDesignStatusBadgeContainer>
              ) : null}
            </S.CardInformationContainer>
          )}
        </S.CardAssetContainer>
      </Center>

      {isFakeTemporaryVirtualCard ? (
        <>
          <Divider marginBottom="space-16" marginTop="space-16" />
          <Text fontWeight={500} size="lg" variant="primary">
            {locales.fakeCard.title}
          </Text>
          <Text marginTop="space-8" size="sm" variant="primary">
            {locales.fakeCard.description1}
          </Text>
          <Text marginTop="space-16" size="sm" variant="primary">
            {locales.fakeCard.description2}
          </Text>
        </>
      ) : null}

      {isLive && isOwner && !isFakeTemporaryVirtualCard ? (
        <CardSecurityDetails creditCard={creditCard} />
      ) : null}

      {isFakeTemporaryVirtualCard ? null : (
        <Stack
          alignItems="stretch"
          marginTop="space-32"
          paddingBottom="space-16"
          spacing="space-16"
        >
          {!isOwner ? (
            <Center>
              <SunshineTag icon="profile">{cardHolderName}</SunshineTag>
            </Center>
          ) : null}

          {cardActions.length > 0 ? (
            <SunshineCard.Group isBoxed={false}>
              {canAliasCard ? (
                <CardAlias
                  alias={alias || undefined}
                  cardHolderName={cardHolderName || ''}
                  creditCardId={creditCardId}
                  isPhysical={isPhysical}
                  key={creditCardId}
                />
              ) : null}
              {isBudget && deactivateAt ? (
                <SunshineCard>
                  <SunshineCard.Slot name="aside">
                    <SquircleIcon name="calendar" />
                  </SunshineCard.Slot>

                  <SunshineCard.Content>
                    <SunshineCard.Title>
                      {locales.deactivationTitle}
                    </SunshineCard.Title>

                    <SunshineCard.Text>
                      {locales.formatString(
                        locales.descriptionWithDate,
                        formatDate(new Date(deactivateAt), 'PP'),
                      )}
                    </SunshineCard.Text>
                  </SunshineCard.Content>
                </SunshineCard>
              ) : null}
              {canUpdateDeactivationDate ? (
                <SunshineCard onClick={onUpdateDeactivationDateClicked}>
                  <SunshineCard.Slot name="aside">
                    <SquircleIcon name="calendar" />
                  </SunshineCard.Slot>

                  <SunshineCard.Content>
                    <SunshineCard.Title>
                      {locales.deactivationTitle}
                    </SunshineCard.Title>

                    <SunshineCard.Text>
                      {deactivateAt
                        ? locales.formatString(
                            locales.descriptionWithDate,
                            formatDate(new Date(deactivateAt), 'PP'),
                          )
                        : locales.description}
                    </SunshineCard.Text>
                  </SunshineCard.Content>

                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
              ) : null}

              {cardActions.map((action) => (
                <SunshineCard key={action.title} onClick={action.onClick}>
                  {action.iconName ? (
                    <SunshineCard.Slot name="aside">
                      <SquircleIcon name={action.iconName} />
                    </SunshineCard.Slot>
                  ) : null}

                  <SunshineCard.Content>
                    <SunshineCard.Title>{action.title}</SunshineCard.Title>

                    <SunshineCard.Text>{action.description}</SunshineCard.Text>
                  </SunshineCard.Content>

                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
              ))}

              <CardLimits
                creditCard={creditCard}
                isEditable={canOrderPaymentCards}
              />
            </SunshineCard.Group>
          ) : null}

          {!isLocked ? (
            <>
              <Divider
                left="-space-24"
                position="relative"
                size="lg"
                // full width
                width="calc(100% + 48px)"
              />

              <CardOptions creditCard={creditCard} />
            </>
          ) : null}
        </Stack>
      )}
    </>
  );
};

export default OperationList;
